<script lang="ts">
import ComingSoonCard from '@/components/cards/new/ComingSoonCard.vue'
import GameCardSkeleton from '@/components/cards/new/GameCardSkeleton.vue'
import InstantWinGameCard from '@/components/cards/new/InstantWinGameCard.vue'
import GameCarousel from '@/components/carousel/GameCarousel.vue'
import MobileGameCarousel from '@/components/carousel/MobileGameCarousel.vue'
import { useInstantWinsStore } from '@/stores/instant-wins'
import type { InstantWinDraw } from '@/types'
import { filterGames } from '@/utils/utils'
import { defineComponent } from 'vue'

/**
 * GamesSection
 */
export default defineComponent({
  components: {
    ComingSoonCard,
    GameCarousel,
    GameCardSkeleton,
    InstantWinGameCard,
    MobileGameCarousel,
  },
  props: {
    columnsPerRow: {
      type: Number,
      default: 4,
    },
  },
  setup() {
    return {
      instantWinsStore: useInstantWinsStore(),
    }
  },
  data() {
    return {
      remainingColumns: 0,
    }
  },
  computed: {
    isLoadingInstantWins(): boolean {
      return this.instantWinsStore.isLoading
    },
    hasInstantWinsError(): boolean {
      return (
        this.instantWinsStore.hasError &&
        this.instantWinsStore.instantWins.size === 0
      )
    },
    instantWinGames(): Array<InstantWinDraw> {
      return filterGames(
        this.instantWinsStore.futureInstantWinDraws.filter(
          (prize) => prize.is_visible ?? true
        ),
        {
          minGames: 0,
          featuredOnly: true,
        }
      )
    },
    isInstantWinsMobile(): boolean {
      return this.columnsPerRow <= 2
    },
  },
  watch: {
    instantWinGames: {
      handler(newItems: Array<InstantWinDraw>) {
        if (this.isInstantWinsMobile && newItems.length > 0) {
          const lastPageLength = newItems.length % 4
          this.remainingColumns = lastPageLength > 0 ? 4 - lastPageLength : 0
        } else {
          this.remainingColumns = this.columnsPerRow - newItems.length
        }
      },
      immediate: true,
    },
    columnsPerRow: {
      handler() {
        if (this.isInstantWinsMobile && this.instantWinGames.length > 0) {
          const lastPageLength = this.instantWinGames.length % 4
          this.remainingColumns = lastPageLength > 0 ? 4 - lastPageLength : 0
        } else {
          this.remainingColumns =
            this.columnsPerRow - this.instantWinGames.length
        }
      },
      immediate: true,
    },
  },
})
</script>

<template>
  <AppSection v-if="isLoadingInstantWins">
    <div class="mb-4 flex items-center gap-3 sm:gap-4">
      <AppIcon :icon="Icons.TicketStar" size="xs" class="sm:h-8 sm:w-8" />
      <h3 class="text-lg font-bold sm:text-2xl">Featured Instant Prizes</h3>
    </div>
    <GameCardSkeleton :count="4" />
  </AppSection>
  <AppSection v-else-if="hasInstantWinsError">
    <div class="py-12 text-center text-slate-600">
      <p class="text-lg">Unable to load games right now.</p>
      <p class="mt-2">Please check back later.</p>
    </div>
  </AppSection>
  <AppSection v-if="instantWinGames.length">
    <!-- Mobile Grid Carousel (2x2 pages, horizontal scroll) -->
    <div v-if="isInstantWinsMobile">
      <MobileGameCarousel
        :games="instantWinGames"
        title="Featured Instant Prizes"
        :icon="Icons.TicketStar"
        :remaining-columns="remainingColumns"
      >
        <template #default="{ page }">
          <InstantWinGameCard
            v-for="prize in page"
            :key="prize.code"
            :game="prize"
          />
        </template>
        <template #empty="{ emptySlots }">
          <ComingSoonCard v-if="emptySlots > 0" :empty-slots="emptySlots" />
        </template>
      </MobileGameCarousel>
    </div>
    <!-- Desktop Carousel (uses GameCarousel component) -->
    <GameCarousel
      v-else
      :key="`instant-${instantWinGames.length}`"
      :prizes="instantWinGames"
      title="Featured Instant Prizes"
      :icon="Icons.TicketStar"
      :view-all-route="{ name: 'prizes', query: { switch: 'instant-wins' } }"
      :rows="2"
      :columns-per-row="columnsPerRow"
      :remaining-columns="remainingColumns"
    >
      <template #default="{ prize }">
        <InstantWinGameCard :key="prize.code" :game="prize" />
      </template>
      <template #empty="{ emptySlots }">
        <ComingSoonCard v-if="emptySlots > 0" :empty-slots="emptySlots" />
      </template>
    </GameCarousel>
  </AppSection>
</template>

<style lang="scss" scoped></style>
